import React, { useState, useEffect, useLayoutEffect } from "react";
import { motion, animate, useMotionValue } from "framer-motion";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { CONTAINER_MARGIN } from "components/Design/Grid/Grid";
import { createListFromArray, fetchItemsFromList } from "utils/linkedList";
import { renderForMobile } from "utils/renderForMobile";
import cx from "classnames";
import { doubleChildren } from "utils/arrayFunctions";
import { SliderArrows } from "components/SliderArrows";

const clamp = (min, max, value) => Math.min(Math.max(value, min), max);

export const Slider = ({ children, cardWidth, centered, cardsToShow }) => {
  const breakpoints = useBreakpoint();
  const isMobile = renderForMobile(breakpoints);

  const [{ current, direction }, setItems] = useState({
    current: createListFromArray(children),
    direction: "none",
  });

  // For pagination
  useEffect(() => {
    if (children.length > cardsToShow) {
      const currentForRotation = createListFromArray(doubleChildren(children));
      setItems({ current: currentForRotation, direction: "left" });
    }
  }, []);

  // Mobile interactions
  const [xValues, setXValues] = useState([]);

  useLayoutEffect(() => {
    const windowWidth = window.innerWidth;
    const flexGap = 16;

    // Calculate offsets
    const extraOnOneSide = (windowWidth - cardWidth - flexGap * 2) / 2;
    const first = 0; // left aligned
    const second = cardWidth - extraOnOneSide + CONTAINER_MARGIN;
    const third = second + cardWidth + flexGap;
    const fourth = third + cardWidth + flexGap;
    const fifth = fourth + cardWidth + flexGap;
    const sixth = fifth + cardWidth + flexGap;
    const values = [first, -second, -third, -fourth, -fifth, -sixth];

    setXValues(values);
  }, []);

  const [index, setIndex] = useState(0);
  const x = useMotionValue(0);

  useEffect(() => {
    if (centered && isMobile && children.length > 2) {
      setIndex(1);
      x.set(xValues[1]);
    }
  }, [xValues]);

  const onDragEnd = (_, { offset, velocity }) => {
    const indexChange = offset.x > 50 ? -1 : offset.x < -50 ? 1 : 0;
    const newIndex = clamp(0, children.length - 1, index + indexChange);

    setIndex(newIndex);
    animate(x, xValues[newIndex]);
  };

  // Desktop interactions
  const goPrevious = () => {
    setItems({ current: current.prev, direction: "left" });
  };

  const goNext = () => {
    setItems({ current: current.next, direction: "right" });
  };

  // Item rendering logic
  const itemsLength = isMobile
    ? children.length
    : children.length > 1
    ? cardsToShow + 2
    : children.length;

  return (
    <div className="lg:relative">
      <div
        className={cx("flex mx-auto gap-16 lg:gap-20 lg:overflow-hidden", {
          "lg:justify-center": itemsLength > 1,
        })}>
        {fetchItemsFromList(current, itemsLength)
          .filter((card, idx, arr) => arr.indexOf(card) === idx)
          .map((child) => {
            return (
              <motion.div
                key={child.key}
                style={{ x }}
                initial={false}
                layout
                transition={{ duration: 0.7, bounce: 0 }}
                draggable
                dragElastic={1}
                drag="x"
                onDragEnd={onDragEnd}>
                {child}
              </motion.div>
            );
          })}
      </div>
      {children.length > cardsToShow && (
        <SliderArrows goPrevious={goPrevious} goNext={goNext} />
      )}
    </div>
  );
};

Slider.defaultProps = {
  cardWidth: 320,
  centered: false,
  cardsToShow: 3,
};
